import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronLeft, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';

type Stage = 'form' | 'complete' | 'banned';

export function RegisterPage() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    passwordConfirm: '',
    email: '',
    displayName: '',
    eflightUsername: '',
    eflightPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState<Stage>('form');

  // 图形验证码
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [needCaptcha, setNeedCaptcha] = useState(false);

  const { addToast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const res = await authApi.getCaptcha();
      setCaptchaToken(res.data.data.captchaToken);
      setCaptchaImage(res.data.data.captchaImage);
      setCaptchaCode('');
    } catch {
      // ignore
    } finally {
      setCaptchaLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      addToast({ title: '请填写必填项', variant: 'warning' });
      return;
    }
    if (!formData.email) {
      addToast({ title: '请填写邮箱地址', variant: 'warning' });
      return;
    }
    if (formData.password !== formData.passwordConfirm) {
      addToast({ title: '两次密码输入不一致', variant: 'warning' });
      return;
    }
    setIsLoading(true);
    try {
      const payload: any = {
        username: formData.username,
        password: formData.password,
        passwordConfirm: formData.passwordConfirm,
        email: formData.email,
        displayName: formData.displayName,
        eflightUsername: formData.eflightUsername,
        eflightPassword: formData.eflightPassword,
      };
      if (needCaptcha && captchaToken && captchaCode) {
        payload.captchaToken = captchaToken;
        payload.captchaCode = captchaCode;
      }
      await authApi.register(payload);
      setStage('complete');
    } catch (error: any) {
      const data = error.response?.data;
      const code = data?.code;

      if (code === 'REGISTRATION_IP_BANNED') {
        setStage('banned');
        return;
      }

      if (code === 'CAPTCHA_REQUIRED') {
        setNeedCaptcha(true);
        fetchCaptcha();
        addToast({ title: '请输入图形验证码', variant: 'warning' });
        return;
      }

      if (code === 'INVALID_CAPTCHA') {
        fetchCaptcha();
        addToast({ title: '验证码错误', description: '请重新输入', variant: 'error' });
        return;
      }

      let message = '注册失败，请稍后重试';
      if (data?.message) {
        message = data.message;
      } else if (data && typeof data === 'object') {
        const first = Object.values(data)[0];
        if (Array.isArray(first) && first.length > 0) message = String(first[0]);
      }
      addToast({ title: '注册失败', description: message, variant: 'error' });

      // 刷新验证码
      if (needCaptcha) fetchCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  if (stage === 'complete') {
    return (
      <div className="text-center space-y-4 py-4">
        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
          <Mail className="w-8 h-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">请验证您的邮箱</h3>
        <p className="text-gray-600 text-sm">
          验证邮件已发送至 <span className="font-medium">{formData.email}</span>，
          请查收邮件中的验证链接或输入 6 位验证码完成注册。
        </p>
        <p className="text-xs text-gray-400">没有收到？请检查垃圾邮件文件夹。</p>
        <Link to="/login" className="block text-blue-600 hover:text-blue-700 text-sm font-medium pt-2">
          返回登录
        </Link>
      </div>
    );
  }

  if (stage === 'banned') {
    return (
      <div className="text-center space-y-4 py-4">
        <h3 className="text-lg font-semibold text-gray-900">注册暂时受限</h3>
        <p className="text-gray-600 text-sm">同一网络注册请求过于频繁，请 24 小时后再试。</p>
        <Link to="/login" className="block text-blue-600 hover:text-blue-700 text-sm font-medium pt-2">
          返回登录
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Link to="/login" className="text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <h2 className="text-xl font-semibold text-gray-900">注册账号</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">用户名 *</Label>
          <Input
            id="username" name="username" type="text"
            placeholder="请输入用户名"
            value={formData.username} onChange={handleChange} disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="password">密码 *</Label>
            <div className="relative">
              <Input
                id="password" name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码"
                value={formData.password} onChange={handleChange} disabled={isLoading}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="passwordConfirm">确认密码 *</Label>
            <Input
              id="passwordConfirm" name="passwordConfirm"
              type={showPassword ? 'text' : 'password'}
              placeholder="请再次输入密码"
              value={formData.passwordConfirm} onChange={handleChange} disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="showPassword" checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)} className="rounded border-gray-300" />
          <Label htmlFor="showPassword" className="text-sm font-normal cursor-pointer">显示密码</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">显示名称</Label>
          <Input id="displayName" name="displayName" type="text"
            placeholder="请输入显示名称" value={formData.displayName} onChange={handleChange} disabled={isLoading} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">邮箱 *</Label>
          <Input id="email" name="email" type="email"
            placeholder="请输入邮箱（用于验证）" value={formData.email} onChange={handleChange} disabled={isLoading} />
        </div>

        {/* ---- 图形验证码 ---- */}
        {needCaptcha && (
          <div className="rounded-lg bg-gray-50 p-3 space-y-2">
            <Label htmlFor="regCaptcha" className="text-sm">图形验证码</Label>
            <div className="flex items-center gap-3">
              {captchaImage ? (
                <img src={captchaImage} alt="验证码" className="h-10 rounded border cursor-pointer flex-shrink-0"
                  onClick={fetchCaptcha} title="点击刷新" />
              ) : (
                <div className="h-10 w-24 bg-gray-200 rounded animate-pulse flex-shrink-0" />
              )}
              <button type="button" onClick={fetchCaptcha} disabled={captchaLoading}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="刷新验证码">
                <RefreshCw className={`w-4 h-4 ${captchaLoading ? 'animate-spin' : ''}`} />
              </button>
              <Input id="regCaptcha" type="text" maxLength={4}
                placeholder="输入验证码"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value.toUpperCase())}
                className="tracking-widest text-center text-lg" disabled={isLoading} />
            </div>
          </div>
        )}

        <div className="border-t pt-4 mt-4">
          <p className="text-sm text-gray-500 mb-4">eFlight 账号信息（可选，可在设置中配置）</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eflightUsername">eFlight 用户名</Label>
              <Input id="eflightUsername" name="eflightUsername" type="text"
                placeholder="请输入 eFlight 用户名" value={formData.eflightUsername} onChange={handleChange} disabled={isLoading} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eflightPassword">eFlight 密码</Label>
              <Input id="eflightPassword" name="eflightPassword" type="password"
                placeholder="请输入 eFlight 密码" value={formData.eflightPassword} onChange={handleChange} disabled={isLoading} />
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />注册中...</>) : ('注册')}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        已有账号？{' '}
        <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
          立即登录
        </Link>
      </p>
    </div>
  );
}
